import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Youtube, Search, Check, Ban, Trash2, ShieldCheck, Link2 } from "lucide-react";

function ChannelAvatar({ src, title }) {
  return src
    ? <img src={src} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
    : <span className="w-10 h-10 rounded-full bg-[#EAE6DF] grid place-items-center shrink-0 text-[#6B7280]"><Youtube size={18} /></span>;
}

export default function VideoControls() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ approved_only: false, approved: [], blocked: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [link, setLink] = useState("");
  const [resolving, setResolving] = useState(false);

  const load = () => { setLoading(true); api.get("/video/settings").then((r) => setSettings(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const approvedIds = new Set(settings.approved.map((c) => c.channel_id));
  const blockedIds = new Set(settings.blocked.map((c) => c.channel_id));
  const statusOf = (cid) => approvedIds.has(cid) ? "approved" : blockedIds.has(cid) ? "blocked" : null;

  const toggleApprovedOnly = async () => {
    try {
      const { data } = await api.put("/video/settings", { approved_only: !settings.approved_only });
      setSettings(data);
      toast.success(data.approved_only ? "Kids will now only see approved channels." : "Kids can now see all safe videos (except blocked).");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const setChannel = async (c, status) => {
    try {
      const { data } = await api.post("/video/channels", { channel_id: c.channel_id, channel_title: c.channel_title, thumbnail: c.thumbnail || "", status });
      setSettings(data);
      toast.success(`${status === "approved" ? "Approved" : "Blocked"} ${c.channel_title}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const removeChannel = async (cid, title) => {
    try { const { data } = await api.delete(`/video/channels/${cid}`); setSettings(data); toast.success(`Removed ${title}`); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const doSearch = async () => {
    if (!search.trim()) return;
    setSearching(true);
    try { const { data } = await api.get(`/video/discover?q=${encodeURIComponent(search)}`); setResults(data.channels || []); if (!data.channels?.length) toast.info("No channels found."); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  const resolveLink = async () => {
    if (!link.trim()) return;
    setResolving(true);
    try { const { data } = await api.post("/video/resolve", { query: link }); setResults(data.channels || []); toast.success("Found channel — approve or block it below."); setLink(""); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setResolving(false); }
  };

  const StatusButtons = ({ c }) => {
    const st = statusOf(c.channel_id);
    return (
      <div className="flex gap-2 shrink-0">
        <button data-testid={`approve-${c.channel_id}`} onClick={() => setChannel(c, "approved")}
          className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${st === "approved" ? "bg-[#81B29A] text-white" : "border border-[#EAE6DF] text-[#6B7280] hover:border-[#81B29A] hover:text-[#81B29A]"}`}><Check size={13} /> Approve</button>
        <button data-testid={`block-${c.channel_id}`} onClick={() => setChannel(c, "blocked")}
          className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${st === "blocked" ? "bg-[#E07A5F] text-white" : "border border-[#EAE6DF] text-[#6B7280] hover:border-[#E07A5F] hover:text-[#E07A5F]"}`}><Ban size={13} /> Block</button>
      </div>
    );
  };

  return (
    <div>
      <TopBar subtitle="Video Controls" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => navigate("/parent")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6"><ArrowLeft size={16} /> Dashboard</button>
        <div className="flex items-center gap-3 mb-8">
          <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[#E07A5F] text-white"><Youtube size={20} /></span>
          <div>
            <h1 className="font-head text-4xl font-bold text-[#3D405B]">Video Controls</h1>
            <p className="text-[#6B7280] text-sm">Decide which YouTube channels your kids can see in "Video help".</p>
          </div>
        </div>

        {/* Approved-only toggle */}
        <div data-testid="approved-only-card" className="rounded-2xl bg-white border border-[#EAE6DF] p-5 flex items-center justify-between gap-4 mb-8">
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-[#81B29A] mt-0.5 shrink-0" />
            <div>
              <p className="font-head font-semibold text-[#3D405B]">Only show approved channels</p>
              <p className="text-sm text-[#6B7280]">When ON, kids will ONLY see videos from channels you've approved. When OFF, they see all safe videos except ones you've blocked.</p>
            </div>
          </div>
          <button data-testid="approved-only-toggle" onClick={toggleApprovedOnly}
            className={`relative shrink-0 w-14 h-8 rounded-full transition-colors ${settings.approved_only ? "bg-[#81B29A]" : "bg-[#D8D2C8]"}`}>
            <span className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all ${settings.approved_only ? "left-7" : "left-1"}`} />
          </button>
        </div>
        {settings.approved_only && settings.approved.length === 0 && (
          <div className="-mt-5 mb-8 rounded-xl bg-[#F4A261]/10 border border-[#F4A261]/30 p-3 text-sm text-[#3D405B]">
            Heads up: "Only approved channels" is ON but you haven't approved any yet — kids will see no videos until you approve some below.
          </div>
        )}

        {/* Find channels */}
        <h2 className="font-head text-lg font-semibold text-[#3D405B] mb-3">Find channels</h2>
        <div className="rounded-2xl bg-white border border-[#EAE6DF] p-5 space-y-4 mb-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
              <input data-testid="channel-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()}
                placeholder="Search a topic (e.g. photosynthesis)…" className="w-full rounded-full border border-[#EAE6DF] pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]" />
            </div>
            <button data-testid="channel-search-btn" onClick={doSearch} disabled={searching} className="rounded-full bg-[#E07A5F] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">{searching ? "…" : "Search"}</button>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link2 size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
              <input data-testid="channel-link" value={link} onChange={(e) => setLink(e.target.value)} onKeyDown={(e) => e.key === "Enter" && resolveLink()}
                placeholder="Or paste a channel link or @handle…" className="w-full rounded-full border border-[#EAE6DF] pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]" />
            </div>
            <button data-testid="channel-link-btn" onClick={resolveLink} disabled={resolving} className="rounded-full border border-[#EAE6DF] px-5 py-2.5 text-sm font-semibold text-[#3D405B] hover:border-[#E07A5F] transition-colors disabled:opacity-60">{resolving ? "…" : "Find"}</button>
          </div>
        </div>
        {results.length > 0 && (
          <div data-testid="channel-results" className="space-y-2 mb-8">
            {results.map((c) => (
              <div key={c.channel_id} className="flex items-center justify-between gap-3 rounded-2xl bg-white border border-[#EAE6DF] px-4 py-2.5">
                <div className="flex items-center gap-3 min-w-0"><ChannelAvatar src={c.thumbnail} /><p className="font-medium text-[#3D405B] truncate">{c.channel_title}</p></div>
                <StatusButtons c={c} />
              </div>
            ))}
          </div>
        )}

        {loading ? null : (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h2 className="font-head text-lg font-semibold text-[#81B29A] mb-3 flex items-center gap-2"><Check size={17} /> Approved ({settings.approved.length})</h2>
              <div className="space-y-2" data-testid="approved-list">
                {settings.approved.length === 0 ? <p className="text-sm text-[#6B7280]">None yet.</p> : settings.approved.map((c) => (
                  <div key={c.channel_id} className="flex items-center justify-between gap-2 rounded-xl bg-white border border-[#EAE6DF] px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0"><ChannelAvatar src={c.thumbnail} /><span className="text-sm text-[#3D405B] truncate">{c.channel_title}</span></div>
                    <button data-testid={`remove-${c.channel_id}`} onClick={() => removeChannel(c.channel_id, c.channel_title)} className="text-[#6B7280] hover:text-[#E07A5F] shrink-0"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-head text-lg font-semibold text-[#E07A5F] mb-3 flex items-center gap-2"><Ban size={17} /> Blocked ({settings.blocked.length})</h2>
              <div className="space-y-2" data-testid="blocked-list">
                {settings.blocked.length === 0 ? <p className="text-sm text-[#6B7280]">None yet.</p> : settings.blocked.map((c) => (
                  <div key={c.channel_id} className="flex items-center justify-between gap-2 rounded-xl bg-white border border-[#EAE6DF] px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0"><ChannelAvatar src={c.thumbnail} /><span className="text-sm text-[#3D405B] truncate">{c.channel_title}</span></div>
                    <button data-testid={`remove-${c.channel_id}`} onClick={() => removeChannel(c.channel_id, c.channel_title)} className="text-[#6B7280] hover:text-[#E07A5F] shrink-0"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
